import { useLayoutEffect, useRef, useState } from "react";
import { Box, Button, Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "i18n-fe";
import SignatureCanvas from "react-signature-canvas";

// #732/#804 — judge signature capture for the HUGE (~75% viewport width)
// signature modal in JudgeManager. #732's hand-rolled canvas mixed
// getBoundingClientRect() (border-box, includes the 1px CSS border) with a
// manually-set canvas.width buffer that ignored both the border and
// devicePixelRatio, so drawn ink appeared nowhere near the cursor (#804).
// react-signature-canvas (wraps signature_pad) derives the drawing buffer
// from the canvas element's own offsetWidth/offsetHeight and scales by
// devicePixelRatio, and reads pointer position from that same element — so
// buffer and pointer are always in agreement. Container-measured width and
// the ~320 height default are preserved (still measured the same way #732
// did it), as is the onChange(dataUrl|null) contract, so JudgeSignatureModal
// needs no changes.
//
// #3383 — the signing area is made obvious (judges kept signing in a corner):
// a heavier 2px boundary, a baseline with an "X" mark to sign on, and a hint
// to use the whole box. ALL of that is plain DOM (the bordered wrapper and the
// pointer-events:none guide overlay), never painted onto the <canvas>, so
// toDataURL() in emitChange() only ever contains the ink. Do not move the
// border or baseline into the canvas element or draw them via its 2D context.
export const SIGNATURE_BORDER_PX = 2;
// Baseline sits this far up from the bottom of the box, leaving room under it
// for descenders and room above it for the whole signature.
const BASELINE_FROM_BOTTOM = "28%";

export default function JudgeSignatureCanvas({ onChange, height = 320 }) {
  const { t } = useTranslation("scoreadmin");
  const theme = useTheme();
  const wrapRef = useRef(null);
  const sigRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [hasInk, setHasInk] = useState(false);

  useLayoutEffect(() => {
    const measure = () => {
      const w = Math.floor(wrapRef.current?.clientWidth ?? 0);
      if (w) setWidth(w);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const emitChange = () => {
    const pad = sigRef.current;
    const empty = !pad || pad.isEmpty();
    setHasInk(!empty);
    onChange(empty ? null : pad.toDataURL("image/png"));
  };

  const clear = () => {
    sigRef.current?.clear();
    setHasInk(false);
    onChange(null);
  };

  const canvasWidth = Math.max(0, width - SIGNATURE_BORDER_PX * 2);
  const canvasHeight = Math.max(0, height - SIGNATURE_BORDER_PX * 2);

  return (
    <Box ref={wrapRef}>
      {width > 0 && (
        <Box
          data-testid="judge-signature-boundary"
          sx={{
            position: "relative",
            boxSizing: "border-box",
            width: `${width}px`,
            height: `${height}px`,
            border: `${SIGNATURE_BORDER_PX}px solid ${theme.palette.grey[700]}`,
            borderRadius: `${theme.shape.borderRadius}px`,
            bgcolor: "#ffffff",
            overflow: "hidden",
          }}
        >
          <SignatureCanvas
            // Remount whenever the measured width changes (eg. window resize
            // while the modal is open) so the drawing buffer is re-derived
            // from the current layout rather than left stale.
            key={`${canvasWidth}x${canvasHeight}`}
            ref={sigRef}
            penColor="#111111"
            clearOnResize={false}
            onEnd={emitChange}
            canvasProps={{
              "data-testid": "judge-signature-canvas",
              style: {
                display: "block",
                width: `${canvasWidth}px`,
                height: `${canvasHeight}px`,
                touchAction: "none",
                cursor: "crosshair",
              },
            }}
          />
          {/* Guide only: a sibling of the canvas, never part of its pixels. */}
          <Box
            aria-hidden="true"
            data-testid="judge-signature-baseline"
            sx={{
              position: "absolute",
              left: "4%",
              right: "4%",
              bottom: BASELINE_FROM_BOTTOM,
              borderBottom: `2px solid ${theme.palette.grey[500]}`,
              pointerEvents: "none",
              userSelect: "none",
            }}
          >
            <Typography
              component="span"
              aria-hidden="true"
              sx={{
                position: "absolute",
                left: 0,
                bottom: 2,
                fontSize: 28,
                lineHeight: 1,
                color: "grey.500",
              }}
            >
              {"\u2715"}
            </Typography>
          </Box>
        </Box>
      )}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
        <Typography variant="body2" color="text.primary">
          {t("judges.signatureModal.drawHint")}
        </Typography>
        <Button size="small" color="inherit" onClick={clear} disabled={!hasInk}>
          {t("judges.signatureModal.clear")}
        </Button>
      </Stack>
    </Box>
  );
}
